import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Building2, Database, Edit, FileSpreadsheet, Plus, Save, Upload, UserRound } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const createEmptyRow = () => ({
  contact_person_name: "",
  company_name: "",
  gst_number: "",
  mobile_number: "",
  email: "",
  customer_type: "Customer",
});

export default function CustomerMaster() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [masters, setMasters] = useState([]);
  const [rows, setRows] = useState([createEmptyRow()]);
  const [filterType, setFilterType] = useState("all");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [editForm, setEditForm] = useState(createEmptyRow());

  useEffect(() => {
    loadMasters();
  }, []);

  const loadMasters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/customer-masters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMasters(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load customer master");
    } finally {
      setLoading(false);
    }
  };

  const visibleMasters = useMemo(() => {
    if (filterType === "all") return masters;
    return masters.filter((item) => item.customer_type === filterType);
  }, [masters, filterType]);

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => (prev.length === 1 ? [createEmptyRow()] : prev.filter((_, rowIndex) => rowIndex !== index)));
  };

  const saveRows = async () => {
    const validEntries = rows.filter(
      (row) => row.company_name.trim() && row.customer_type,
    );

    if (validEntries.length === 0) {
      toast.error("Please add at least one valid customer master row");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/customer-masters/bulk`,
        { entries: validEntries },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Saved ${response.data.created_count + response.data.updated_count} customer master record(s)`,
      );
      setRows([createEmptyRow()]);
      loadMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save customer master");
    } finally {
      setSaving(false);
    }
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/customer-masters/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success(
        `Imported ${response.data.created_count + response.data.updated_count} customer master record(s)`,
      );
      loadMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import customer master file");
    } finally {
      event.target.value = "";
      setImporting(false);
    }
  };

  const openEditDialog = (master) => {
    setEditingMaster(master);
    setEditForm({
      contact_person_name: master.contact_person_name || "",
      company_name: master.company_name || "",
      gst_number: master.gst_number || "",
      mobile_number: master.mobile_number || "",
      email: master.email || "",
      customer_type: master.customer_type || "Customer",
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingMaster) return;

    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      await axios.put(`${API}/customer-masters/${editingMaster.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Customer master updated successfully");
      setEditDialogOpen(false);
      setEditingMaster(null);
      loadMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update customer master");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="customer-master-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              Customer Master
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Create vendor and customer masters manually, in bulk rows, or through Excel upload.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="text-xs"
              onClick={addRow}
            >
              <Plus size={14} className="mr-1.5" />
              Add Row
            </Button>
            <label className="inline-flex">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={importFile}
              />
              <span className="inline-flex items-center rounded-md bg-white border border-gray-300 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50">
                <FileSpreadsheet size={14} className="mr-1.5" />
                {importing ? "Importing..." : "Excel Upload"}
              </span>
            </label>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary-100">
                <Database size={18} className="text-primary-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Total Masters</div>
                <div className="text-lg font-bold text-gray-900">{masters.length}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Building2 size={18} className="text-orange-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Customers</div>
                <div className="text-lg font-bold text-gray-900">
                  {masters.filter((item) => item.customer_type === "Customer").length}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <UserRound size={18} className="text-green-600" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Vendors</div>
                <div className="text-lg font-bold text-gray-900">
                  {masters.filter((item) => item.customer_type === "Vendor").length}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-bold text-gray-800">
              Bulk Create Customer Master
            </CardTitle>
            <CardDescription className="text-xs text-gray-500">
              Add multiple rows in one go. Required columns for Excel: `Contact Person Name`, `Company Name`, `Gst number`, `Mobile number`, `Email`, `Cusomer Type`.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row, index) => (
              <div key={index} className="grid lg:grid-cols-6 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Contact Person</Label>
                  <Input
                    value={row.contact_person_name}
                    onChange={(e) => updateRow(index, "contact_person_name", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Company Name</Label>
                  <Input
                    value={row.company_name}
                    onChange={(e) => updateRow(index, "company_name", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">GST Number</Label>
                  <Input
                    value={row.gst_number}
                    onChange={(e) => updateRow(index, "gst_number", e.target.value.toUpperCase())}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Mobile</Label>
                  <Input
                    value={row.mobile_number}
                    onChange={(e) => updateRow(index, "mobile_number", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={row.email}
                    onChange={(e) => updateRow(index, "email", e.target.value)}
                    className="h-9 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Customer Type</Label>
                  <div className="flex gap-2">
                    <Select
                      value={row.customer_type}
                      onValueChange={(value) => updateRow(index, "customer_type", value)}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Customer">Customer</SelectItem>
                        <SelectItem value="Vendor">Vendor</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-9 text-xs"
                      onClick={() => removeRow(index)}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={saveRows}
                disabled={saving}
                className="bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white text-xs"
              >
                <Save size={14} className="mr-1.5" />
                {saving ? "Saving..." : "Save All Rows"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 bg-gradient-to-br from-white to-gray-50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm font-bold text-gray-800">
                Existing Customer Master
              </CardTitle>
              <CardDescription className="text-xs text-gray-500">
                These masters are used during invoice verification to calculate match probability.
              </CardDescription>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px] h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Customer">Customers</SelectItem>
                <SelectItem value="Vendor">Vendors</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 text-center text-sm text-gray-500">Loading customer master...</div>
            ) : visibleMasters.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">
                No customer master records found for this workspace yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Company</TableHead>
                      <TableHead className="text-xs">Contact Person</TableHead>
                      <TableHead className="text-xs">GST</TableHead>
                      <TableHead className="text-xs">Mobile</TableHead>
                      <TableHead className="text-xs">Email</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleMasters.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="text-sm font-medium text-gray-900">
                          {item.company_name}
                        </TableCell>
                        <TableCell className="text-sm">{item.contact_person_name || "-"}</TableCell>
                        <TableCell className="text-sm font-mono">{item.gst_number || "-"}</TableCell>
                        <TableCell className="text-sm">{item.mobile_number || "-"}</TableCell>
                        <TableCell className="text-sm">{item.email || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {item.customer_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => openEditDialog(item)}
                          >
                            <Edit size={12} className="mr-1.5" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Edit Customer Master</DialogTitle>
            <DialogDescription className="text-xs">
              Update this customer or vendor master record.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSave} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Contact Person Name</Label>
                <Input
                  value={editForm.contact_person_name}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      contact_person_name: e.target.value,
                    }))
                  }
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Company Name</Label>
                <Input
                  value={editForm.company_name}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      company_name: e.target.value,
                    }))
                  }
                  className="h-9 text-xs"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">GST Number</Label>
                <Input
                  value={editForm.gst_number}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      gst_number: e.target.value.toUpperCase(),
                    }))
                  }
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Mobile Number</Label>
                <Input
                  value={editForm.mobile_number}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      mobile_number: e.target.value,
                    }))
                  }
                  className="h-9 text-xs"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="h-9 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Customer Type</Label>
                <Select
                  value={editForm.customer_type}
                  onValueChange={(value) =>
                    setEditForm((prev) => ({ ...prev, customer_type: value }))
                  }
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="Vendor">Vendor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-primary-500 to-orange-500 text-white"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
